import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { ContractStatus } from "@/lib/data";
import {
  Building2,
  Calendar,
  CalendarClock,
  FileText,
  Hash,
} from "lucide-react";
import { StatusBadge } from "@/components/ui/badges";
import type { ContractDetailResponse } from "@/types/contracts";
import { cn } from "@/lib/utils";

const LEGAL_STATE_TO_STATUS: Record<string, ContractStatus> = {
  DRAFT: "Draft",
  ACTIVE: "Active",
  EXPIRED: "Expired",
  TERMINATED: "Terminated",
};

function getInitials(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

function formatDate(value: string | null): string {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function ContractMetadata({
  contract: c,
}: {
  contract: ContractDetailResponse;
}) {
  const status = c.legalState ? LEGAL_STATE_TO_STATUS[c.legalState] : null;
  return (
    <Card className="p-5">
      <h2 className="text-sm font-semibold text-foreground">
        Contract Metadata
      </h2>
      <Separator className="my-4 border-b-[1px]" />

      <dl className="space-y-4">
        <Field icon={FileText} label="Name">
          {/* <span className="font-normal text-foreground">{c.title}</span> */}
          <TruncatedValue
            value={c.title}
            className="font-normal text-foreground"
          />
        </Field>
        <Field icon={Building2} label="Counterparty">
          <TruncatedValue value={c.counterparty} />
        </Field>
        <Field icon={Hash} label="ID">
          <TruncatedValue value={c.id} className="font-mono text-xs" />
        </Field>
        {/* <Field icon={DollarSign} label="Value">
          <span className="font-medium text-foreground">{c.value}</span>
        </Field> */}

        <div className="flex items-center gap-3">
          <dt className="flex w-28 shrink-0 items-center gap-2 text-sm text-muted-foreground">
            <span className="flex size-7 items-center justify-center rounded-md bg-accent text-accent-foreground">
              <FileText className="size-3.5" />
            </span>
            Status
          </dt>
          <dd className="min-w-0 flex-1 text-sm text-foreground">
            {status ? (
              <StatusBadge status={status} />
            ) : (
              <span className="text-muted-foreground">Not set</span>
            )}
          </dd>
        </div>

        <Field icon={Calendar} label="Eff. Date">
          <span className="font-normal text-foreground">
            {formatDate(c.effectiveDate)}
          </span>
        </Field>
        <Field icon={CalendarClock} label="Exp. Date">
          <span className="font-normal text-foreground">
            {formatDate(c.expirationDate)}
          </span>
        </Field>
      </dl>

      <Separator className="my-4 border-b-[1px]" />

      <div className="flex items-center justify-between gap-3">
        <dt className="mb-2 text-sm text-muted-foreground">Tags</dt>
        <dd className="flex flex-wrap gap-1.5">
          {c.tags.map((t) => (
            <Badge
              key={t}
              variant="secondary"
              className="font-medium rounded-full border border-border/50 px-2 py-1 text-xs text-foreground"
            >
              {t}
            </Badge>
          ))}
        </dd>
      </div>

      <Separator className="my-4 border-b-[1px]" />

      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <dt className="flex w-28 shrink-0 items-center gap-2 text-sm text-muted-foreground">
            Uploaded By
          </dt>
          <dd className="flex min-w-0 flex-1 items-center gap-2 text-sm text-foreground ">
            <Avatar className="size-6 shrink-0">
              <AvatarFallback className="bg-primary text-[10px] font-semibold text-primary-foreground">
                {getInitials(c.uploadedByName)}
              </AvatarFallback>
            </Avatar>
            <TruncatedValue
              value={c.uploadedByName}
              className="min-w-0 flex-1 font-medium text-foreground"
            />
          </dd>
        </div>
        <div className="flex items-center justify-between gap-3">
          <dt className="flex w-28 shrink-0 items-center gap-2 text-sm text-muted-foreground">
            Created Date
          </dt>
          <dd className="min-w-0 flex-1 text-sm text-foreground">
            {formatDate(c.createdAt)}
          </dd>
        </div>
      </div>
    </Card>
  );
}

function Field({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3">
      <dt className="flex w-28 shrink-0 items-center gap-2 text-sm text-muted-foreground">
        <span className="flex size-7 items-center justify-center rounded-md bg-accent text-accent-foreground">
          <Icon className="size-3.5" />
        </span>
        {label}
      </dt>
      <dd className="min-w-0 flex-1 text-sm text-foreground">{children}</dd>
    </div>
  );
}

function TruncatedValue({
  value,
  className,
}: {
  value: string;
  className?: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={<span className={cn("block truncate", className)} />}
      >
        {value}
      </TooltipTrigger>
      <TooltipContent>{value}</TooltipContent>
    </Tooltip>
  );
}
